import { execFileSync } from 'node:child_process'
import fs from 'node:fs'
import path from 'node:path'

export type PackageManager = 'pnpm' | 'npm' | 'yarn' | 'bun'

const LOCKFILE_MAP: Record<string, PackageManager> = {
  'pnpm-lock.yaml': 'pnpm',
  'package-lock.json': 'npm',
  'yarn.lock': 'yarn',
  'bun.lockb': 'bun',
  'bun.lock': 'bun'
}

export function detectPackageManager(cwd: string): PackageManager {
  // Walk up the directory tree to find lockfiles (handles monorepos)
  let dir = path.resolve(cwd)
  const root = path.parse(dir).root
  while (dir !== root) {
    for (const [lockfile, pm] of Object.entries(LOCKFILE_MAP)) {
      if (fs.existsSync(path.join(dir, lockfile))) {
        return pm
      }
    }
    // Check packageManager field in package.json at this level
    const pkgPath = path.join(dir, 'package.json')
    if (fs.existsSync(pkgPath)) {
      try {
        const pkg = JSON.parse(fs.readFileSync(pkgPath, 'utf-8')) as Record<string, unknown>
        if (typeof pkg.packageManager === 'string') {
          const name = pkg.packageManager.split('@')[0]
          if (name === 'pnpm' || name === 'npm' || name === 'yarn' || name === 'bun') {
            return name
          }
        }
      } catch {
        // ignore parse errors
      }
    }
    dir = path.dirname(dir)
  }

  return 'npm'
}

export function installCommand(pm: PackageManager): string {
  return pm === 'yarn' ? 'yarn' : `${pm} install`
}

export function addCommand(pm: PackageManager, deps: string[], dev = false): string {
  const devFlag = dev ? (pm === 'yarn' ? '--dev' : '-D') : ''

  switch (pm) {
    case 'pnpm':
      return ['pnpm', 'add', devFlag, ...deps].filter(Boolean).join(' ')
    case 'yarn':
      return ['yarn', 'add', devFlag, ...deps].filter(Boolean).join(' ')
    case 'bun':
      return ['bun', 'add', devFlag, ...deps].filter(Boolean).join(' ')
    default:
      return ['npm', 'install', devFlag, ...deps].filter(Boolean).join(' ')
  }
}

export function runCommand(pm: PackageManager, script: string): string {
  switch (pm) {
    case 'pnpm':
      return `pnpm ${script}`
    case 'yarn':
      return `yarn ${script}`
    case 'bun':
      return `bun run ${script}`
    default:
      return `npm run ${script}`
  }
}

export function execPm(pm: PackageManager, args: string[], cwd: string): void {
  execFileSync(pm, args, { cwd, stdio: 'inherit' })
}

/**
 * Returns the binary + leading args for the exact `create-next-app` package.
 * npm owns this bootstrap so an unpinned project-level pnpm cannot block before
 * BetterStart has a package.json where it can enforce the supported pnpm version.
 */
export function createNextAppCommand(): { bin: string; prefix: string[] } {
  return {
    bin: 'npm',
    prefix: ['exec', '--yes', '--package=create-next-app@latest', '--', 'create-next-app']
  }
}
